import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarDays, Users, ClipboardList, Dumbbell, CalendarRange, BarChart3, MessageCircle, GraduationCap } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AdminSeasons from "./AdminSeasons";
import AdminGroups from "./AdminGroups";
import SessionList from "./SessionList";
import ExerciseLibrary from "./ExerciseLibrary";
import SeasonTimeline from "./SeasonTimeline";
import AdminAttendanceStats from "./AdminAttendanceStats";
import AdminCoaches from "./AdminCoaches";
import GroupChat from "@/components/training/GroupChat";

export default function AdminTraining() {
  const { t } = useTranslation("admin");
  const [tab, setTab] = useState("seasons");
  const [selectedSeasonId, setSelectedSeasonId] = useState<string | null>(null);
  const [selectedChatGroupId, setSelectedChatGroupId] = useState<string | null>(null);
  const { organization } = useOrganization();

  const { data: groups = [] } = useQuery({
    queryKey: ["admin-chat-groups", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("training_groups")
        .select("id, name")
        .eq("organization_id", organization!.id)
        .order("name");
      return data ?? [];
    },
    enabled: !!organization && tab === "chat",
  });

  const navigateToGroups = (seasonId: string) => {
    setSelectedSeasonId(seasonId);
    setTab("groups");
  };

  const activeChatGroupId = selectedChatGroupId ?? (groups.length > 0 ? groups[0].id : null);
  const activeChatGroup = groups.find(g => g.id === activeChatGroupId);

  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList className="mb-4 flex-wrap">
        <TabsTrigger value="seasons" className="gap-1">
          <CalendarDays className="h-4 w-4" /> {t("trainingTabs.seasons")}
        </TabsTrigger>
        <TabsTrigger value="groups" className="gap-1">
          <Users className="h-4 w-4" /> {t("trainingTabs.groups")}
        </TabsTrigger>
        <TabsTrigger value="sessions" className="gap-1">
          <ClipboardList className="h-4 w-4" /> {t("trainingTabs.sessions")}
        </TabsTrigger>
        <TabsTrigger value="exercises" className="gap-1">
          <Dumbbell className="h-4 w-4" /> {t("trainingTabs.exercises")}
        </TabsTrigger>
        <TabsTrigger value="timeline" className="gap-1">
          <CalendarRange className="h-4 w-4" /> {t("trainingTabs.timeline")}
        </TabsTrigger>
        <TabsTrigger value="coaches" className="gap-1">
          <GraduationCap className="h-4 w-4" /> {t("trainingTabs.coaches")}
        </TabsTrigger>
        <TabsTrigger value="chat" className="gap-1">
          <MessageCircle className="h-4 w-4" /> {t("trainingTabs.chat")}
        </TabsTrigger>
        <TabsTrigger value="stats" className="gap-1">
          <BarChart3 className="h-4 w-4" /> {t("trainingTabs.stats")}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="seasons">
        <AdminSeasons onNavigateToGroups={navigateToGroups} />
      </TabsContent>
      <TabsContent value="groups">
        <AdminGroups initialSeasonId={selectedSeasonId} onBack={() => setTab("seasons")} />
      </TabsContent>
      <TabsContent value="sessions">
        <SessionList initialSeasonId={selectedSeasonId} />
      </TabsContent>
      <TabsContent value="exercises">
        <ExerciseLibrary />
      </TabsContent>
      <TabsContent value="timeline">
        <SeasonTimeline initialSeasonId={selectedSeasonId} />
      </TabsContent>
      <TabsContent value="coaches">
        <AdminCoaches />
      </TabsContent>
      <TabsContent value="chat">
        <div className="space-y-3">
          {groups.length > 1 && (
            <Select value={activeChatGroupId ?? ""} onValueChange={setSelectedChatGroupId}>
              <SelectTrigger className="w-[250px]">
                <SelectValue placeholder="Välj grupp…" />
              </SelectTrigger>
              <SelectContent>
                {groups.map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {activeChatGroupId && activeChatGroup ? (
            <GroupChat groupId={activeChatGroupId} groupName={activeChatGroup.name} isCoachOrAdmin />
          ) : (
            <p className="text-sm text-muted-foreground">Inga grupper hittades.</p>
          )}
        </div>
      </TabsContent>
      <TabsContent value="stats">
        <AdminAttendanceStats initialSeasonId={selectedSeasonId} />
      </TabsContent>
    </Tabs>
  );
}
